/** Staging forum host — used when REACT_APP_XENFORO_PUBLIC_ORIGIN is unset (local/dev). */
const DEFAULT_XENFORO_PUBLIC_ORIGIN = 'https://test9.reef2reef.com';

/**
 * Public XenForo / Reef2Reef forum origin (no trailing slash).
 * Used for Community tab and other handoffs to the main forum.
 *
 * @param {string|null|undefined} [origin]
 * @returns {string}
 */
export const xenforoPublicOrigin = (
  origin = process.env.REACT_APP_XENFORO_PUBLIC_ORIGIN || DEFAULT_XENFORO_PUBLIC_ORIGIN
) => {
  const base = typeof origin === 'string' ? origin.trim().replace(/\/+$/, '') : '';
  return base || DEFAULT_XENFORO_PUBLIC_ORIGIN;
};

/**
 * Build a XenForo direct-message compose URL for a forum username.
 *
 * Origin comes from REACT_APP_XENFORO_PUBLIC_ORIGIN (e.g. https://test9.reef2reef.com
 * in staging, https://www.reef2reef.com in production). Trailing slashes are stripped.
 * Falls back to the staging forum origin when the env var is missing.
 *
 * @param {string|null|undefined} xfUsername XenForo username
 * @param {string|null|undefined} [origin]
 * @returns {string|null} Absolute DM URL, or null when username missing
 */
export const xenforoDirectMessageUrl = (
  xfUsername,
  origin = process.env.REACT_APP_XENFORO_PUBLIC_ORIGIN || DEFAULT_XENFORO_PUBLIC_ORIGIN
) => {
  const username = typeof xfUsername === 'string' ? xfUsername.trim() : '';
  const base = typeof origin === 'string' ? origin.trim().replace(/\/+$/, '') : '';
  if (!username || !base) {
    return null;
  }
  return `${base}/direct-messages/add?to=${encodeURIComponent(username)}`;
};

/**
 * XenForo direct-message inbox URL (conversation list, not compose).
 *
 * @param {string|null|undefined} [origin]
 * @returns {string}
 */
export const xenforoDirectMessagesInboxUrl = (
  origin = process.env.REACT_APP_XENFORO_PUBLIC_ORIGIN || DEFAULT_XENFORO_PUBLIC_ORIGIN
) => `${xenforoPublicOrigin(origin)}/direct-messages`;

/**
 * XenForo account registration URL.
 *
 * @param {string|null|undefined} [origin]
 * @returns {string}
 */
export const xenforoRegisterUrl = (
  origin = process.env.REACT_APP_XENFORO_PUBLIC_ORIGIN || DEFAULT_XENFORO_PUBLIC_ORIGIN
) => `${xenforoPublicOrigin(origin)}/register/`;

/**
 * XenForo account upgrades URL (supporter / sponsor purchases).
 *
 * @param {string|null|undefined} [origin]
 * @returns {string}
 */
export const xenforoAccountUpgradesUrl = (
  origin = process.env.REACT_APP_XENFORO_PUBLIC_ORIGIN || DEFAULT_XENFORO_PUBLIC_ORIGIN
) => `${xenforoPublicOrigin(origin)}/account/upgrades`;

/**
 * Preferred Contact Seller target: synced `publicData.xfUsername`, else display name
 * (covers accounts that have not re-logged since xfUsername sync shipped).
 *
 * @param {{ publicData?: { xfUsername?: string }, displayName?: string }|null|undefined} profile
 * @returns {string|null}
 */
export const profileContactUsername = profile => {
  const fromXf =
    typeof profile?.publicData?.xfUsername === 'string' ? profile.publicData.xfUsername.trim() : '';
  if (fromXf) {
    return fromXf;
  }
  const fromDisplay = typeof profile?.displayName === 'string' ? profile.displayName.trim() : '';
  return fromDisplay || null;
};
